import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../auth/auth.context'
import { authService } from '../../services'
import PageLayout from '../../components/layout/page-layout'
import { Container } from '../../components/ui/container'
import { contrastText } from '../../lib/utils'
import { LanguageSwitcher } from '../../components/language-switcher'

export default function UserProfile() {
  const { t } = useTranslation()
  const { user, company, isLoading: authLoading } = useAuth()

  const isPasswordUser = user?.role === 'ADMIN' || user?.role === 'SUPERADMIN'

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [changingPassword, setChangingPassword] = useState(false)
  const [passwordError, setPasswordError] = useState<string>()
  const [passwordSuccess, setPasswordSuccess] = useState(false)

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setPasswordError(undefined)
    setPasswordSuccess(false)

    if (newPassword.length < 8) {
      setPasswordError(t('auth.passwordMinLengthHint'))
      return
    }

    if (newPassword !== confirmPassword) {
      setPasswordError(t('auth.passwordsMismatch'))
      return
    }

    setChangingPassword(true)

    try {
      await authService.changePassword(currentPassword, newPassword)
      setPasswordSuccess(true)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (err: any) {
      setPasswordError(
        err?.userMessage || err?.response?.data?.message || t('auth.changePasswordFailed')
      )
    } finally {
      setChangingPassword(false)
    }
  }

  return (
    <PageLayout
      headerProps={{
        title: t('profile.title'),
        subtitle: t('profile.subtitle'),
      }}
      isLoading={authLoading}
      error={!user && !authLoading ? new Error(t('errors.userNotFound')) : undefined}
      loadingMessage={t('profile.loading')}
      errorMessage={t('errors.loadProfile')}
    >
      <Container>
        <div className='space-y-6'>
          <div className='highlight-card rounded-[var(--radius-xl)] p-6'>
            <h2 className='text-xl font-semibold mb-4 text-neutral-900'>
              {t('profile.userInformation')}
            </h2>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm font-medium text-neutral-700 mb-1'>
                  {t('common.firstName')}
                </label>
                <div className='px-3 py-2 bg-surface-secondary border border-primary-100 rounded-lg'>
                  <span className='text-neutral-900'>{user?.firstName}</span>
                </div>
              </div>

              <div>
                <label className='block text-sm font-medium text-neutral-700 mb-1'>
                  {t('common.lastName')}
                </label>
                <div className='px-3 py-2 bg-surface-secondary border border-primary-100 rounded-lg'>
                  <span className='text-neutral-900'>{user?.lastName}</span>
                </div>
              </div>

              <div>
                <label className='block text-sm font-medium text-neutral-700 mb-1'>
                  {t('common.email')}
                </label>
                <div className='px-3 py-2 bg-surface-secondary border border-primary-100 rounded-lg'>
                  <span className='text-neutral-900'>{user?.email}</span>
                </div>
              </div>

              <div>
                <label className='block text-sm font-medium text-neutral-700 mb-1'>
                  {t('common.role')}
                </label>
                <div className='px-3 py-2 bg-surface-secondary border border-primary-100 rounded-lg'>
                  <span className='text-neutral-900'>
                    {user?.role === 'ADMIN' ? t('roles.administrator') : t('roles.employee')}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {isPasswordUser && (
            <div className='highlight-card rounded-[var(--radius-xl)] p-6'>
              <h2 className='text-xl font-semibold mb-1 text-neutral-900'>
                {t('auth.changePasswordTitle')}
              </h2>
              <p className='text-sm text-neutral-600 mb-4'>{t('auth.changePasswordSubtitle')}</p>

              <form onSubmit={handleChangePassword} className='space-y-4 max-w-md'>
                <div>
                  <label htmlFor='currentPassword' className='block text-sm font-medium text-neutral-700 mb-1'>
                    {t('auth.currentPassword')}
                  </label>
                  <input
                    type='password'
                    id='currentPassword'
                    value={currentPassword}
                    onChange={e => setCurrentPassword(e.target.value)}
                    placeholder={t('auth.currentPasswordPlaceholder')}
                    required
                    disabled={changingPassword}
                    className='w-full px-3 py-2 border border-primary-100 rounded-lg bg-white focus:ring-2 focus:ring-sky-200 focus:border-sky-300 disabled:opacity-50'
                  />
                </div>

                <div>
                  <label htmlFor='newPassword' className='block text-sm font-medium text-neutral-700 mb-1'>
                    {t('auth.newPassword')}
                  </label>
                  <input
                    type='password'
                    id='newPassword'
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    placeholder={t('auth.newPasswordPlaceholder')}
                    required
                    minLength={8}
                    disabled={changingPassword}
                    className='w-full px-3 py-2 border border-primary-100 rounded-lg bg-white focus:ring-2 focus:ring-sky-200 focus:border-sky-300 disabled:opacity-50'
                  />
                </div>

                <div>
                  <label htmlFor='confirmPassword' className='block text-sm font-medium text-neutral-700 mb-1'>
                    {t('auth.confirmPassword')}
                  </label>
                  <input
                    type='password'
                    id='confirmPassword'
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    placeholder={t('auth.passwordRepeatPlaceholder')}
                    required
                    minLength={8}
                    disabled={changingPassword}
                    className='w-full px-3 py-2 border border-primary-100 rounded-lg bg-white focus:ring-2 focus:ring-sky-200 focus:border-sky-300 disabled:opacity-50'
                  />
                </div>

                {passwordError && (
                  <p className='text-sm text-red-600' role='alert'>
                    {passwordError}
                  </p>
                )}

                {passwordSuccess && (
                  <p className='text-sm text-emerald-600' role='status'>
                    {t('auth.changePasswordSuccess')}
                  </p>
                )}

                <button
                  type='submit'
                  disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
                  className='px-4 py-2 bg-sky-600 hover:bg-sky-700 disabled:bg-neutral-300 text-white rounded-lg font-medium transition-colors'
                >
                  {changingPassword ? t('common.processing') : t('auth.changePasswordSubmit')}
                </button>
              </form>
            </div>
          )}

          <div className='highlight-card rounded-[var(--radius-xl)] p-6'>
            <h2 className='text-xl font-semibold mb-4 text-neutral-900'>
              {t('profile.languageSettings')}
            </h2>

            <div>
              <label className='block text-sm font-medium text-neutral-700 mb-2'>
                {t('profile.preferredLanguage')}
              </label>
              <LanguageSwitcher />
            </div>
          </div>

          {company && (
            <div className='highlight-card rounded-[var(--radius-xl)] p-6'>
              <h2 className='text-xl font-semibold mb-4 text-neutral-900'>
                {t('profile.companyInformation')}
              </h2>

              <div className='flex items-center space-x-4'>
                {company?.logoUrl ? (
                  <img
                    src={company.logoUrl}
                    alt={t('profile.companyLogo')}
                    className='w-16 h-16 object-contain border border-primary-100 rounded-lg bg-white/80'
                    onError={e => {
                      e.currentTarget.style.display = 'none'
                      e.currentTarget.nextElementSibling?.classList.remove('hidden')
                    }}
                  />
                ) : null}
                <div
                  className={`w-16 h-16 rounded-xl flex items-center justify-center font-bold text-xl`}
                  style={{
                    backgroundColor: company.color || '#38bdf8',
                    color: contrastText(company.color || '#38bdf8'),
                  }}
                >
                  {company.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h3 className='text-lg font-semibold text-neutral-900'>{company.name}</h3>
                  {company.description && <p className='text-neutral-600'>{company.description}</p>}
                </div>
              </div>
            </div>
          )}
        </div>
      </Container>
    </PageLayout>
  )
}
